import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getAvatarInitials, getAvatarInitialsColor } from '../../utils/avatar-initials.util';
import { UserAvatarService } from '../../../services/user-avatar.service';
import { AvatarSyncService } from '../../../services/avatar-sync.service';

@Component({
  selector: 'app-user-avatar',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span
      class="avatar-root"
      [class.website]="website"
      [class.honesty-website]="isHonestyWebsite()"
      [class.honesty-ai]="isHonestyAI()"
      [class.honesty-crm]="isHonestyCRM()"
      [class.animate-pulse]="isLoading()"
      [style.width]="size"
      [style.height]="size"
      [class.avatar-unassigned]="showUnassignedPlaceholder()"
      [style.background]="getBackgroundColor()"
      [style.color]="avatarInitialTextColor()"
    >
      @if (website) {
        <i class="pi pi-globe"></i>
      } @else if (!hasResolved() || isLoading()) {
        <span class="h-full w-full rounded-full bg-slate-300/70"></span>
      } @else if (avatarUrl()) {
        <img [src]="avatarUrl()" alt="" class="block h-full w-full rounded-full object-cover" />
      } @else if (isHonestySpecial()) {
        <img src="assets/img/honesty-head.png" alt="Honesty" class="block h-[88%] w-[88%] rounded-full object-cover" />
      } @else if (showUnassignedPlaceholder()) {
        <i class="pi pi-user avatar-unassigned-icon" aria-hidden="true"></i>
      } @else {
        <span class="avatar-initials">{{ initials() }}</span>
      }
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex-shrink: 0;
    }

    .avatar-root {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 9999px;
      background: linear-gradient(135deg, #C5D0E6 0%, #B8C5E0 100%);
      color: #6B7AA0;
      font-size: 0.75rem;
      overflow: hidden;
      flex-shrink: 0;
    }

    .avatar-root.website {
      background: linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%);
      color: #6B7280;
    }

    .avatar-initials {
      display: block;
      font-size: var(--avatar-initials-font-size, 0.56rem) !important;
      line-height: 1;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.005em;
      white-space: nowrap;
      transform: translateY(-0.02em);
      color: #ffffff !important;
    }

    .avatar-root.avatar-unassigned {
      background: #e2e8f0 !important;
    }

    .avatar-unassigned-icon {
      font-size: 0.65em;
      color: #64748b;
      line-height: 1;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAvatarComponent implements OnChanges {
  @Input() userId: string | number | null | undefined;
  @Input() displayName: string | null | undefined;
  @Input() website = false;
  @Input() size = '1.5rem';
  /**
   * When true, never use Honesty Website/AI/CRM logo or gradient — only photo or initials.
   * Use in compact UIs (e.g. notifications) where the branded image is illegible at ~1.5rem
   * and the light gradient can look like a blank circle.
   */
  @Input() skipBrandedSystemAvatar = false;

  avatarUrl = signal<string | null>(null);
  isLoading = signal(false);
  hasResolved = signal(false);

  private readonly avatarService = inject(UserAvatarService);
  private readonly avatarSync = inject(AvatarSyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private lastRequestKey = '';

  constructor() {
    this.avatarSync.avatarUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadAvatar());
  }

  ngOnChanges(): void {
    this.reloadAvatar();
  }

  private reloadAvatar(): void {
    if (this.website) {
      this.avatarUrl.set(null);
      this.isLoading.set(false);
      this.hasResolved.set(true);
      return;
    }

    const id = this.normalizedUserId();
    if (!id) {
      this.avatarUrl.set(null);
      this.isLoading.set(false);
      this.hasResolved.set(true);
      return;
    }

    this.isLoading.set(true);
    this.hasResolved.set(false);
    this.lastRequestKey = id;
    this.avatarService
      .getAvatarDataUrl(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url) => {
        if (this.lastRequestKey !== id) {
          return;
        }
        this.avatarUrl.set(url);
        this.isLoading.set(false);
        this.hasResolved.set(true);
        this.changeDetector.markForCheck();
      });
  }

  showUnassignedPlaceholder(): boolean {
    if (this.website) {
      return false;
    }
    if (!this.hasResolved() || this.isLoading()) {
      return false;
    }
    if (this.avatarUrl()) {
      return false;
    }
    if (this.isHonestySpecial()) {
      return false;
    }
    if (this.normalizedUserId()) {
      return false;
    }
    return this.isPlaceholderOrEmptyDisplayName(this.displayName);
  }

  avatarInitialTextColor(): string | null {
    if (this.website) {
      return null;
    }
    if (this.showUnassignedPlaceholder()) {
      return null;
    }
    if (!this.avatarUrl() && !this.isHonestySpecial()) {
      return '#ffffff';
    }
    return null;
  }

  initials(): string {
    const name = (this.displayName ?? '').trim();
    if (!name || this.isPlaceholderOrEmptyDisplayName(this.displayName)) {
      return '?';
    }

    const parts = name.split(/\s+/).filter(Boolean) as [string, ...string[]];
    const firstName = parts[0];
    const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
    return getAvatarInitials(firstName, lastName);
  }

  initialsColor(): string {
    return getAvatarInitialsColor(this.normalizedUserId() || '?');
  }

  isHonestyWebsite(): boolean {
    const name = (this.displayName ?? '').trim().toLowerCase();
    return name.includes('honesty website') || name === 'website';
  }

  isHonestyAI(): boolean {
    const name = (this.displayName ?? '').trim().toLowerCase();
    return name.includes('honesty ai') || name === 'honesty ai';
  }

  isHonestyCRM(): boolean {
    const name = (this.displayName ?? '').trim().toLowerCase();
    return name.includes('honesty crm') || name === 'crm';
  }

  isHonestySpecial(): boolean {
    if (this.skipBrandedSystemAvatar) {
      return false;
    }
    return this.isHonestyWebsite() || this.isHonestyAI() || this.isHonestyCRM();
  }

  getBackgroundColor(): string | null {
    if (this.website) {
      return null; 
    }
    if (this.showUnassignedPlaceholder()) {
      return '#e2e8f0';
    }
    if (this.avatarUrl()) {
      return null; 
    }
    if (this.isHonestyWebsite()) {
      return 'linear-gradient(135deg, #f3e8ff 0%, #7e22ce 100%)'; // purple-100 to purple-700
    }
    if (this.isHonestyAI()) {
      return 'linear-gradient(135deg, #dbeafe 0%, #1d4ed8 100%)'; // blue-100 to blue-700
    }
    if (this.isHonestyCRM()) {
      return 'linear-gradient(135deg, #dcfce7 0%, #15803d 100%)'; // green-100 to green-700
    }
    return this.initialsColor();
  }

  private normalizedUserId(): string {
    const normalized = String(this.userId ?? '').trim();
    if (!normalized || normalized === '--' || normalized.toLowerCase() === 'null' || normalized.toLowerCase() === 'undefined') {
      return '';
    }
    return normalized;
  }

  private isPlaceholderOrEmptyDisplayName(name: string | null | undefined): boolean {
    const t = (name ?? '').trim();
    if (!t) {
      return true;
    }
    const lower = t.toLowerCase();
    return ['--', '—', '–', '-', 'n/a', 'na', 'none'].includes(lower);
  }

}
